import type { Action, ListMailFrequency } from '@thecompaniesapi/shared/client'

export const useListsStore = defineStore('lists', () => {
  const api = useApi()
  const { toggleModal } = useOverlays()

  const lists = ref<List[]>([])

  const firstListWithUnseenActions = computed(() => {
    return lists.value.find(list => list.unseenActions?.length)
  })

  const unseenActionsCount = computed(() => {
    let count = 0

    for (const list of lists.value) {
      for (const action of list.unseenActions || []) {
        count = count + (action?.result?.domains?.length || 1)
      }
    }

    return count
  })

  const createList = async (params: {
    companyListId?: null | number
    dynamic?: boolean
    imported?: boolean
    maxCompanies?: number
    name: string
    processInitialized?: boolean
    query?: SegmentationCondition[]
    similarDomains?: string[]
  }) => {
    try {
      const { data: list } = await api.lists.create({
        dynamic: params.dynamic,
        imported: params.imported,
        maxCompanies: params.maxCompanies,
        name: params.name,
        processInitialized: params.processInitialized,
        query: params.query,
        similarDomains: params.similarDomains,
      })

      addOrReplaceLists({ lists: [list] })

      return list
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const exportList = async (params: {
    email: string
    emailsOnly?: boolean
    exportType: FileExportType
    fileName: string
    listId: number
  }) => {
    try {
      const { data: list } = await api.lists.export(params.listId, {
        email: params.email,
        emailsOnly: params.emailsOnly,
        exportType: params.exportType,
        fileName: params.fileName,
      })

      addOrReplaceLists({ lists: [list] })

      return list
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchLists = async (params: {
    page?: number
    size?: number
  }) => {
    try {
      params.size = params.size || 10

      const { data: { lists } } = await api.lists.fetch({
        page: params.page,
        size: params.size,
      })

      addOrReplaceLists({ lists })

      return lists
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const removeList = async (params: {
    listId: number
  }) => {
    try {
      const { data: list } = await api.lists.delete(params.listId)

      lists.value = lists.value.filter(item => item.id !== list.id)
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateList = async (params: {
    dynamic?: boolean
    lastSeen?: boolean
    listId: number
    mailFrequency?: ListMailFrequency
    maxCompanies?: null | number
    name?: string
    query?: SegmentationCondition[]
    resync?: boolean
  }) => {
    try {
      const { data: list } = await api.lists.update(params.listId, {
        dynamic: params.dynamic,
        lastSeen: params.lastSeen,
        mailFrequency: params.mailFrequency,
        maxCompanies: params.maxCompanies,
        name: params.name,
        query: params.query,
        resync: params.resync,
      })

      addOrReplaceLists({ lists: [list] })
    }
    catch (error: any) {
      // Dynamic lists require a subscription
      if (error?.response?.data?.message === 'subscriptionRequired') {
        toggleModal({
          opened: true,
          title: 'signals',
          type: 'subscription',
        })
      }

      return Promise.reject(error)
    }
  }

  const toggleCompaniesFromList = async (params: {
    action?: 'attach' | 'detach'
    columnsMatched?: { [key: string]: string }
    companies?: CompanyV2[]
    country?: string
    currentCompanies?: CompanyV2[]
    domains?: string[]
    isFirstPage?: boolean
    isLastPage?: boolean
    listId?: number
    maxCompanies?: number
    onToggleCompanies?: (params: {
      action: 'attach' | 'detach'
      companies: CompanyV2[]
      listId: number
    }) => void
    query?: SegmentationCondition[]
    tableCompanies?: CompanyV2[]
  }) => {
    try {
      let list: List | null = null

      // Save companies from query
      if (params.query && params.listId) {
        const response = await api.lists.companies.toggleWithQuery(params.listId, {
          maxCompanies: params.maxCompanies,
          query: params.query,
        })

        list = response.data
      }
      // Save companies from imported file
      else if (params.columnsMatched && params.companies && params.listId) {
        const response = await api.lists.companies.toggleWithImport(params.listId, {
          columnsMatched: params.columnsMatched,
          companies: params.companies,
          country: params.country,
          isFirstPage: params.isFirstPage || false,
          isLastPage: params.isLastPage || false,
        })

        list = response.data
      }
      // Save companies from similar domains
      else if (params.domains && params.listId) {
        const response = await api.lists.companies.toggleWithSimilar(params.listId, { domains: params.domains })

        list = response.data
      }
      // Save companies from selection
      else if (params.companies && params.action && params.listId) {
        const response = await api.lists.companies.toggle(params.listId, {
          action: params.action,
          companyIds: params.companies
            .filter(company => company?.meta?.mysqlId)
            .map(company => company.meta!.mysqlId!),
        })

        // Call callback
        params.onToggleCompanies?.({
          action: params.action,
          companies: params.companies,
          listId: params.listId,
        })

        list = response.data
      }

      // Update with received list from server
      if (list) {
        addOrReplaceLists({ lists: [list] })
      }

      // Check if list should be marked as processing for user feedback
      if (params.columnsMatched || params.query || params.domains) {
        const listToUpdate = lists.value.find(item => item.id === params.listId)

        if (listToUpdate) {
          listToUpdate.processActive = true
          listToUpdate.processingAt = new Date().toString()
        }
      }
    }
    catch (error: any) {
      return Promise.reject(error)
    }
  }

  const toggleIntegrationFromList = async (params: {
    action: 'attach' | 'detach'
    integrationSettingsId: number
    listId: number
  }) => {
    try {
      const { data: list } = await api.lists.integrations.toggle(params.listId, params.integrationSettingsId, { action: params.action })

      addOrReplaceLists({ lists: [list] })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const addListActions = (params: {
    actions: Action[]
    listId: number
  }) => {
    lists.value = lists.value.map((list) => {
      if (list.id !== params.listId) {
        return list
      }

      const unseenActions = list.unseenActions || []

      for (const action of params.actions) {
        if (!unseenActions.find(unseenAction => unseenAction.id === action.id)) {
          unseenActions.push(action)
        }
      }

      return {
        ...list,
        unseenActions,
      }
    })
  }

  const addOrReplaceLists = (params: {
    lists: List[]
  }) => {
    for (const list of params.lists) {
      const index = lists.value.findIndex((item) => {
        return item.id === list.id
      })

      index === -1 ? lists.value.push(list) : lists.value.splice(index, 1, list)
    }
  }

  const resetStore = () => {
    lists.value = []
  }

  const sendTestEmail = async (params: { listId: number }) => {
    return await api.lists.sendTestEmail(params.listId)
  }

  return {
    addListActions,
    addOrReplaceLists,
    createList,
    exportList,
    fetchLists,
    firstListWithUnseenActions,
    lists,
    removeList,
    resetStore,
    sendTestEmail,
    toggleCompaniesFromList,
    toggleIntegrationFromList,
    unseenActionsCount,
    updateList,
  }
})
